import React, { createContext, useEffect, useState } from "react";

import {
  orderedLoanPrograms,
  OwnedRealEstate,
  SandboxInputsForREOStatuses,
  SandboxRequest,
  SandboxRequestREO,
  SandboxResponse,
  SandboxResponseProgram,
  SandboxReworkRequest,
} from "@eave/shared/types/api";

import Spinner from "web_modules/shared/Spinner";

import { getInputsForReos, loadInitialInputs, submitScenario, submitRework } from "./api";

const sortPrograms = (programs: SandboxResponseProgram[]): SandboxResponseProgram[] =>
  orderedLoanPrograms
    .map((name: string) => programs.find((p: SandboxResponseProgram) => p.name === name))
    .filter(p => p);

interface ISandboxScenarioContext {
  initialInputs: SandboxRequest | null;
  submitting: boolean;
  error: boolean;
  request: SandboxRequest | null;
  results: SandboxResponse | null;
  ownedRealEstate: OwnedRealEstate[];
  getInputsForReos(req: SandboxRequestREO[]): Promise<SandboxInputsForREOStatuses>;
  resetResults(): void;
  submitScenario(req: SandboxRequest): Promise<SandboxResponse>;
  submitReworkRequest(req: SandboxReworkRequest): Promise<{}>;
}

export const SandboxScenarioContext = createContext<ISandboxScenarioContext>({
  initialInputs: null,
  submitting: false,
  error: false,
  request: null,
  results: null,
  ownedRealEstate: null,
  submitScenario: (_: SandboxRequest): Promise<SandboxResponse> =>
    Promise.resolve({} as SandboxResponse),
  resetResults: () => null,
  getInputsForReos: (_: SandboxRequestREO[]): Promise<SandboxInputsForREOStatuses> =>
    Promise.resolve({
      total_assets: 0,
      monthly_liabilities: 0,
      monthly_income: 0,
      reos: [],
    }),
  submitReworkRequest: (_: SandboxReworkRequest): Promise<{}> => Promise.resolve({}),
});

const SandboxScenarioProvider = ({ appId, ownedRealEstate, children }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [initialInputs, setInitialInputs] = useState<SandboxRequest | null>(null);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [request, setRequest] = useState<SandboxRequest | null>(null);
  const [results, setResults] = useState<SandboxResponse | null>(null);

  const submit = async (req: SandboxRequest) => {
    setSubmitting(true);
    setError(false);
    setRequest(null);
    setResults(null);

    try {
      const newResults = await submitScenario(appId, req);
      setResults({
        ...newResults,
        programs: sortPrograms(newResults.programs),
      });
      setRequest(req);
      return newResults;
    } catch (e) {
      setError(true);
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    setLoading(true);

    loadInitialInputs(appId)
      .then(newInitialInputs => {
        setInitialInputs(newInitialInputs);
        return submit(newInitialInputs);
      })
      .catch(e => {
        window.alert("There was a problem loading this page! Please contact eng.");
        throw e;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [appId]);

  const getInputs = async (reos: SandboxRequestREO[]): Promise<SandboxInputsForREOStatuses> => {
    setSubmitting(true);
    setError(false);

    try {
      const newInputs = await getInputsForReos(appId, reos);
      return newInputs;
    } catch (e) {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitting(false);
    setError(false);
    setResults(null);
  };

  if (loading) {
    return <Spinner active />;
  }

  const submitReworkRequest = async (req: SandboxReworkRequest): Promise<{}> => {
    setSubmitting(true);
    setError(false);

    try {
      const res = await submitRework(appId, req);
      return res;
    } catch (e) {
      setError(true);
      return {};
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SandboxScenarioContext.Provider
      value={{
        ownedRealEstate,
        initialInputs,
        submitting,
        error,
        request,
        results,
        getInputsForReos: getInputs,
        submitScenario: submit,
        resetResults: reset,
        submitReworkRequest,
      }}
    >
      {children}
    </SandboxScenarioContext.Provider>
  );
};

export default SandboxScenarioProvider;
